import { useState } from 'react';
import { Save } from 'lucide-react';

import { productionApi } from 'api/production';
import { useQueryParams } from 'components/production/components/AisleContent/hooks/useQueryParams';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import ProductSpotlightForm from 'shared/components/ProductSpotlightForm';

import BannerEditorActions from '../BannerEditor/BannerEditorActions';
import { useEditorMutation } from '../hooks/useEditorMutation';

import ProductSpotlightEditorSummary from './ProductSpotlightEditorSummary';
import { transformProductSpotlightToFormData } from './utils';

/**
 * Displays an editor for Product Spotlight content.
 *
 * @param {string} aisleID - Parent aisle ID.
 * @param {number} aisleLevel - Aisle level (1 or 2).
 * @param {Object} carousel - Product spotlight carousel data.
 * @param {Function} handleDeleteNewEditor - Callback function to delete a new editor.
 * @param {string} initialView - Initial view, defaults to 'summary'.
 * @param {Function} refetchCarousels - Function to refetch carousels from the parent component.
 * @param {Array} sellableItems - Sellable items for the product picker.
 */
export default function ProductSpotlightEditor({
    aisleID,
    aisleLevel,
    carousel,
    handleDeleteNewEditor,
    initialView = 'summary',
    refetchCarousels,
    sellableItems = [],
}) {
    const { queryParamsData } = useQueryParams();

    const [view, setView] = useState(initialView);
    const [formData, setFormData] = useState(() =>
        carousel.id ? transformProductSpotlightToFormData(carousel) : carousel,
    );
    const { executeMutation, isSaving } = useEditorMutation({
        entityName: 'Product spotlight',
        refetch: refetchCarousels,
    });

    const toggleView = () => {
        setView((prev) => (prev === 'summary' ? 'form' : 'summary'));
    };

    const handleFormChange = (updatedData) => {
        setFormData(updatedData);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        await executeMutation({
            action: async () => {
                await productionApi.createUpdateCarouselV2({
                    packGroupID: queryParamsData.packGroupID,
                    carousels: [
                        {
                            ...formData,
                            ...(carousel.id ? { carouselID: carousel.id } : {}),
                            [aisleLevel === 1 ? 'l1AisleID' : 'l2AisleID']: aisleID,
                            featured: false,
                            type: 'NORMAL',
                            items: formData.items.map((item) => ({
                                ...item,
                                id: item.sellableItemID ? item.sellableItemID : item.id,
                            })),
                        },
                    ],
                });
                if (!carousel.id) {
                    handleDeleteNewEditor?.();
                }
            },
            failure: 'Failed to save product spotlight',
        });
    };

    const handleDelete = async () => {
        if (!carousel.id) {
            handleDeleteNewEditor();
            return;
        }
        await executeMutation({
            action: async () => {
                const response = await productionApi.deleteCarousel(carousel.id);
                if (!response.status) {
                    throw new Error(response.msg || 'Failed to delete product spotlight');
                }
            },
            loading: 'Deleting product spotlight...',
            success: 'Product spotlight deleted successfully',
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    {carousel.name ? (
                        <div className="flex items-center gap-1">
                            <h3>{carousel.name}</h3>
                            <span className="ml-2 text-sm text-muted-foreground">(Product Spotlight)</span>
                            <span className="ml-2 font-normal text-sm text-muted-foreground">id: {carousel.id}</span>
                        </div>
                    ) : (
                        <h3>Product Spotlight</h3>
                    )}
                    <BannerEditorActions onDelete={handleDelete} toggleView={toggleView} view={view} />
                </CardTitle>
            </CardHeader>
            <CardContent>
                {view === 'summary' && <ProductSpotlightEditorSummary data={carousel} formData={formData} />}
                {view === 'form' && (
                    <form className="flex flex-col gap-6" onSubmit={handleSave}>
                        <ProductSpotlightForm
                            data={formData}
                            disabled={isSaving}
                            onChange={handleFormChange}
                            sellableProducts={sellableItems}
                        />
                        <Button className="self-end w-content" disabled={isSaving}>
                            <Save />
                            Save changes
                        </Button>
                    </form>
                )}
            </CardContent>
        </Card>
    );
}
